/**
 * Verifier Portal — /verifier
 * Secure environment for accredited third-party verifiers.
 * Shows login if not authenticated, dashboard + workspace if authenticated.
 */
import React, { useState, useEffect, useCallback } from "react";
import { VerifierProvider, useVerifier } from "@/context/VerifierContext";
import {
  Shield, Check, AlertTriangle, X, RefreshCw, FileText,
  ChevronRight, LogOut, Info, CheckCircle2, AlertCircle
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const STATUS_CFG = {
  assigned:      { color:"bg-amber-500",  text:"text-amber-700 bg-amber-50 border-amber-200",  label:"Assigned"       },
  in_review:     { color:"bg-blue-500",   text:"text-blue-700 bg-blue-50 border-blue-200",     label:"In Review"      },
  pending_info:  { color:"bg-indigo-400", text:"text-indigo-700 bg-indigo-50 border-indigo-200",label:"Clarif. Needed"},
  approved:      { color:"bg-emerald-500",text:"text-emerald-700 bg-emerald-50 border-emerald-200",label:"Approved"   },
  rejected:      { color:"bg-red-400",    text:"text-red-700 bg-red-50 border-red-200",        label:"Rejected"       },
};
const fmt = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.assigned;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.color}`} /> {c.label}
    </span>
  );
}

// ── Verifier Login ─────────────────────────────────────────────────────────
function VerifierLogin() {
  const { login }     = useVerifier();
  const [email, setE] = useState("");
  const [pass,  setP] = useState("");
  const [err,   setErr]= useState(null);
  const [loading,setL] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setL(true); setErr(null);
    try { await login(email, pass); }
    catch (err) { setErr(err.message); }
    setL(false);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4" data-testid="verifier-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="DeCarbonIQ" className="h-8 w-auto mx-auto mb-4" style={{filter:"brightness(0) invert(1)"}} />
          <h1 className="text-2xl font-bold text-white">Verifier Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Accredited third-party verification environment</p>
        </div>
        <div className="bg-white rounded-xl p-8">
          <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-xl mb-5">
            <Shield size={14} className="text-indigo-600 flex-shrink-0" />
            <p className="text-xs text-indigo-700">Restricted access. Authorized verifiers only.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            {[["email","Email","email",email,setE],["password","Password","password",pass,setP]].map(([id,label,type,val,set])=>(
              <div key={id}>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{label}</label>
                <input type={type} value={val} onChange={e=>set(e.target.value)} required
                  className="w-full h-11 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            ))}
            {err && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>}
            <button type="submit" disabled={loading}
              className="w-full h-11 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-60">
              {loading ? "Authenticating..." : "Sign In to Verifier Portal"}
            </button>
          </form>
        </div>
        <p className="text-xs text-slate-600 text-center mt-4">
          Not a verifier? <a href="/" className="text-slate-400 hover:text-white">Return to DeCarbonIQ</a>
        </p>
      </div>
    </div>
  );
}

// ── Verification Workspace Panel ────────────────────────────────────────────
function WorkspacePanel({ assignmentId, onClose, onDecisionSubmitted }) {
  const { verifierFetch } = useVerifier();
  const [detail,   setDetail]  = useState(null);
  const [loading,  setLoading] = useState(true);
  const [tab,      setTab]     = useState("overview");
  const [decision, setDec]     = useState("approve");
  const [statement,setStmt]    = useState("");
  const [notes,    setNotes]   = useState("");
  const [submitting,setSub]    = useState(false);
  const [submitErr, setSubErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    verifierFetch(`/verifier/assignments/${assignmentId}`)
      .then(async d => {
        // Also fetch engagement for commercial tab (uses client_router, need platform auth header workaround)
        // For now, pass assignment_id so Commercial tab shows a message
        setDetail({ ...d, engagement: null });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [assignmentId, verifierFetch]);

  const handleDecision = async () => {
    if (!statement.trim()) { setSubErr("A formal verification statement is required."); return; }
    setSub(true); setSubErr(null);
    try {
      await verifierFetch(`/verifier/assignments/${assignmentId}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision, verification_statement: statement, decision_notes: notes }),
      });
      onDecisionSubmitted();
      onClose();
    } catch (err) { setSubErr(err.message); }
    setSub(false);
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end">
      <div className="w-full lg:w-[640px] bg-white flex items-center justify-center">
        <RefreshCw size={24} className="text-slate-400 animate-spin" />
      </div>
    </div>
  );
  if (!detail) return null;

  const { overview, data_review, evidence, validation, anomalies, actions, audit_log, verifier, assignment } = detail;
  const isTerminal = ["approved","rejected"].includes(assignment?.status);
  const TABS = ["overview","methodology","data","evidence","actions","commercial","decision","audit"];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" data-testid="verifier-workspace">
      <div className="w-full lg:w-[680px] bg-white flex flex-col h-full overflow-hidden">
        <div className="bg-[#0B1220] px-5 py-4 flex items-start justify-between flex-shrink-0">
          <div>
            <p className="text-xs text-indigo-400 uppercase tracking-wider">Third-Party Verification</p>
            <p className="text-white font-bold">{overview?.org_name} — {overview?.assessment_title}</p>
            <p className="text-xs text-slate-400 mt-0.5">{verifier?.org} · {verifier?.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={assignment?.status} />
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg"><X size={15}/></button>
          </div>
        </div>

        <div className="border-b border-slate-200 flex overflow-x-auto flex-shrink-0">
          {TABS.map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 capitalize ${tab===t?"border-indigo-600 text-indigo-600":"border-transparent text-slate-400 hover:text-slate-700"}`}>
              {t.replace(/_/g," ")}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab==="overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[["Organization",overview?.org_name],["Sector",overview?.sector],["Country",overview?.country],
                  ["FY",overview?.reporting_year?`FY${overview.reporting_year}`:"—"],
                  ["Confidence",`${fmt(overview?.confidence_score,0)}%`],["FRS",`${fmt(overview?.frs_score,0)}/100`],
                  ["Current Level",overview?.current_level?.replace(/_/g," ")],
                  ["Requested",overview?.requested_level?.replace(/_/g," ")],
                ].map(([l,v])=>(
                  <div key={l} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-400">{l}</p><p className="text-sm font-semibold text-slate-900">{String(v||"—")}</p>
                  </div>
                ))}
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-indigo-700 mb-1">Verification Scope</p>
                <p className="text-xs text-indigo-600">{assignment?.verification_scope}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-500 mb-1">Verifier Credentials</p>
                <p className="text-xs text-slate-600">{verifier?.org} · {verifier?.role?.replace(/_/g," ")}</p>
                {(verifier?.accreditations||[]).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {verifier.accreditations.map(a=>(
                      <span key={a} className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">{a}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab==="methodology" && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Methodology (Read-Only)</p>
                {[["Standard",detail?.methodology?.standard],["Version",detail?.methodology?.version],["Hash",detail?.methodology?.hash]].map(([l,v])=>(
                  <div key={l} className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-xs text-slate-400">{l}</span>
                    <span className="text-xs font-mono font-medium text-slate-700 truncate max-w-[280px]">{v||"—"}</span>
                  </div>
                ))}
              </div>
              {detail?.methodology?.category_breakdown && Object.keys(detail.methodology.category_breakdown).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Category Breakdown</p>
                  <div className="space-y-1">
                    {Object.entries(detail.methodology.category_breakdown).map(([cat,kg])=>(
                      <div key={cat} className="flex justify-between text-xs py-1 border-b border-slate-50">
                        <span className="text-slate-500 capitalize">{cat}</span>
                        <span className="font-medium tabular-nums text-slate-700">{((kg||0)/1000).toFixed(3)} tCO₂e</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab==="data" && (
            <div className="space-y-4">
              {data_review?.total_tco2e != null && (
                <div className="grid grid-cols-4 gap-2">
                  {[["Total",`${fmt(data_review.total_tco2e,1)}t`],["Scope 1",`${fmt(data_review.scope_1,2)}t`],["Scope 2",`${fmt(data_review.scope_2,2)}t`],["Scope 3",`${fmt(data_review.scope_3,2)}t`]].map(([l,v])=>(
                    <div key={l} className="bg-slate-50 rounded-lg p-3 text-center"><p className="text-sm font-bold">{v}</p><p className="text-[10px] text-slate-400">{l}</p></div>
                  ))}
                </div>
              )}
              {validation && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Validation Status</p>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${validation.is_valid?"bg-emerald-50 text-emerald-700":"bg-red-50 text-red-700"}`}>
                    {validation.is_valid?<Check size={12}/>:<AlertTriangle size={12}/>}
                    {validation.is_valid?`Passed — ${validation.warning_count} warning(s)`:`${validation.issue_count} error(s), ${validation.warning_count} warning(s)`}
                  </div>
                  {(validation.issues||[]).slice(0,3).map((i,idx)=>(
                    <div key={idx} className="text-xs px-3 py-1.5 bg-red-50 rounded border border-red-200 text-red-700 mt-1">{i.message}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab==="evidence" && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-2">{(evidence||[]).filter(e=>e.is_verified).length}/{(evidence||[]).length} verified. Coverage: {fmt(detail.evidence_coverage)}%</p>
              {(evidence||[]).length===0 ? (
                <div className="text-center py-8 text-slate-400"><FileText size={24} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No evidence on file</p></div>
              ) : evidence.map((e,i)=>(
                <div key={i} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${e.is_verified?"bg-emerald-50":"bg-slate-100"}`}>
                    <FileText size={14} className={e.is_verified?"text-[#059669]":"text-slate-400"}/>
                  </div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#0F172A] truncate">{e.name}</p><p className="text-xs text-slate-400">{e.type||"Document"}</p></div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${e.is_verified?"bg-emerald-50 text-emerald-700":"bg-slate-100 text-slate-500"}`}>{e.is_verified?"✓ Verified":"Pending"}</span>
                </div>
              ))}
            </div>
          )}

          {tab==="actions" && (
            <div className="space-y-2">
              {(actions||[]).map((a,i)=>(
                <div key={i} className="p-3 bg-white border border-slate-200 rounded-xl">
                  <p className="text-sm font-semibold text-slate-900 capitalize mb-2">{(a.action_type||"").replace(/_/g," ")}</p>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {[["CapEx",`$${((a.capex||0)).toLocaleString()}`],["ROI",`${fmt(a.roi_pct,0)}%`],["Payback",`${fmt(a.payback_years,1)} yrs`]].map(([l,v])=>(
                      <div key={l} className="bg-slate-50 rounded p-1.5 text-center"><p className="font-bold">{v}</p><p className="text-slate-400">{l}</p></div>
                    ))}
                  </div>
                </div>
              ))}
              {(actions||[]).length===0 && <p className="text-sm text-slate-400 text-center py-8">No actions generated</p>}
            </div>
          )}

          {tab==="commercial" && (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-indigo-700 mb-1">Commercial Status (Read-Only)</p>
                <p className="text-xs text-indigo-600">
                  The commercial terms for this engagement are managed by the DeCarbonIQ team. 
                  Verification work should only proceed when status is "Approved to Proceed".
                </p>
              </div>
              {detail?.engagement ? (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Commercial Status</span>
                    <span className="text-xs font-semibold text-[#0F172A] capitalize">{detail.engagement.commercial_status?.replace(/_/g," ")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-400">Approved to Proceed</span>
                    <span className={`text-xs font-semibold ${detail.engagement.commercial_status==="approved_to_proceed"?"text-emerald-600":"text-amber-600"}`}>
                      {detail.engagement.commercial_status==="approved_to_proceed"?"Yes — You may begin":"Not yet approved"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400 bg-slate-50 rounded-xl p-4">
                  No commercial engagement on file for this assignment. Contact the DeCarbonIQ team.
                </p>
              )}
            </div>
          )}

          {tab==="decision" && (
            <div className="space-y-4">
              {isTerminal ? (
                <div className={`p-4 rounded-xl border ${assignment.status==="approved"?"bg-emerald-50 border-emerald-200":"bg-red-50 border-red-200"}`}>
                  <p className={`text-sm font-bold mb-1 ${assignment.status==="approved"?"text-emerald-700":"text-red-700"}`}>
                    Decision: {assignment.status.charAt(0).toUpperCase()+assignment.status.slice(1)}
                  </p>
                  {assignment.verification_statement && <p className="text-xs text-slate-600 italic">"{assignment.verification_statement}"</p>}
                </div>
              ) : (
                <>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Verification Decision</p>
                  <div className="space-y-2">
                    {[["approve","Approve — Issue Third-Party Verification"],["request_clarification","Request Clarification"],["reject","Reject — Verification Denied"]].map(([val,lbl])=>(
                      <label key={val} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${decision===val?val==="approve"?"border-emerald-400 bg-emerald-50":val==="reject"?"border-red-300 bg-red-50":"border-amber-300 bg-amber-50":"border-slate-200 hover:border-slate-300"}`}>
                        <input type="radio" name="ver-decision" value={val} checked={decision===val} onChange={()=>setDec(val)} className="accent-indigo-600" />
                        <span className="text-sm font-medium text-[#0F172A]">{lbl}</span>
                      </label>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 block">Formal Verification Statement * (required)</label>
                    <textarea value={statement} onChange={e=>setStmt(e.target.value)} rows={4}
                      placeholder="This assessment has been independently verified in accordance with ISO 14064-3. The reported GHG inventory is... (formal statement required)"
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 block">Additional Notes (optional)</label>
                    <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
                      placeholder="Any additional observations..."
                      className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                  </div>
                  {submitErr && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitErr}</p>}
                  <button onClick={handleDecision} disabled={submitting || !statement.trim()}
                    data-testid="submit-verifier-decision-btn"
                    className="w-full py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-50">
                    {submitting ? "Submitting..." : "Submit Formal Decision"}
                  </button>
                  <p className="text-xs text-slate-400 text-center">This decision is permanent, audited, and immutable.</p>
                </>
              )}
            </div>
          )}

          {tab==="audit" && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Verification Audit Trail</p>
              {(audit_log||[]).length===0 ? <p className="text-xs text-slate-400 text-center py-8">No audit entries</p> : (
                <div className="space-y-2" data-testid="verifier-audit-log">
                  {audit_log.map((l,i)=>(
                    <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-1 bg-indigo-300 rounded-full flex-shrink-0"/>
                      <div>
                        <p className="text-xs font-semibold text-[#0F172A] capitalize">{(l.action||"").replace(/_/g," ")}</p>
                        {l.comment && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{l.comment.slice(0,200)}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">{l.timestamp?.slice(0,16).replace("T"," ")}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 cursor-pointer" onClick={onClose} />
    </div>
  );
}

// ── Verifier Dashboard ─────────────────────────────────────────────────────
function VerifierDashboard() {
  const { verifierUser, logout, verifierFetch } = useVerifier();
  const [assignments, setAssignments] = useState([]);
  const [stats,       setStats]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await verifierFetch("/verifier/assignments");
      setAssignments(d.assignments || []);
      setStats(d.stats || {});
    } catch {}
    setLoading(false);
  }, [verifierFetch]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="verifier-dashboard">
      <nav className="bg-[#0B1220] h-14 flex items-center justify-between px-6 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{filter:"brightness(0) invert(1)"}}/>
          <span className="text-xs text-indigo-400 font-semibold uppercase tracking-wider">Verifier Portal</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 hidden sm:block">{verifierUser?.org_name} · {verifierUser?.first_name} {verifierUser?.last_name}</span>
          <button onClick={logout} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-3 py-1.5 border border-slate-700 rounded-lg">
            <LogOut size={12} /> Sign Out
          </button>
        </div>
      </nav>

      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Verification Assignments</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage third-party verification for assigned assessments.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[["Total",stats.total],["Assigned",stats.assigned,true],["In Review",stats.in_review],["Completed",stats.completed]].map(([l,v,acc])=>(
              <div key={l} className={`rounded-xl border p-4 ${acc?"bg-amber-50 border-amber-200":"bg-white border-slate-200"}`}>
                <p className="text-xs uppercase tracking-wider text-slate-400">{l}</p>
                <p className={`text-2xl font-bold mt-1 ${acc?"text-amber-700":"text-[#0F172A]"}`}>{v}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="verifier-queue">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {["Company","Sector","Country","FRS","Confidence","Status","Due Date","Action"].map(h=>(
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? [...Array(3)].map((_,i)=>(
                  <tr key={i}>{[...Array(8)].map((_,j)=><td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse"/></td>)}</tr>
                )) : assignments.length===0 ? (
                  <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                    <Shield size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No assignments</p>
                  </td></tr>
                ) : assignments.map(a=>(
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(a.id)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-900">{a.org_name}</p>
                      <p className="text-xs text-slate-400">{a.assessment_title}{a.reporting_year?` · FY${a.reporting_year}`:""}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{a.sector||"—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{a.country||"—"}</td>
                    <td className="px-4 py-3 text-sm font-bold text-slate-700">{a.frs_score!=null?fmt(a.frs_score,0):"—"}</td>
                    <td className="px-4 py-3">
                      {a.confidence_score!=null
                        ? <div className="flex items-center gap-1.5"><div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{width:`${a.confidence_score}%`}}/></div><span className="text-xs">{fmt(a.confidence_score,0)}%</span></div>
                        : "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={a.status}/></td>
                    <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{a.due_date?.slice(0,10)||"—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button className="flex items-center gap-1 text-xs text-indigo-600 font-medium hover:text-indigo-700">
                        Open <ChevronRight size={12}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <WorkspacePanel
          assignmentId={selected}
          onClose={() => setSelected(null)}
          onDecisionSubmitted={load}
        />
      )}
    </div>
  );
}

// ── Entry Point (auto-routes login ↔ dashboard) ────────────────────────────
function VerifierPortalContent() {
  const { verifierUser, loading } = useVerifier();
  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  return verifierUser ? <VerifierDashboard /> : <VerifierLogin />;
}

export default function VerifierPortal() {
  return (
    <VerifierProvider>
      <VerifierPortalContent />
    </VerifierProvider>
  );
}
